import { FC, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
} from '@chakra-ui/react';
import { FiUpload, FiFileText, FiSearch, FiX } from 'react-icons/fi';
import { usePDFAnalysisStore } from '@/stores/pdfAnalysisStore';
import { PDF_ANALYSIS_CONFIG } from '@/services/ai/config';

interface PDFAnalyzerProps {
  /** ユーザーID */
  userId: string;
  /** 解析完了時のコールバック */
  onAnalysisComplete?: () => void;
}

/**
 * PDF解析コンポーネント
 * ドキュメントをアップロードしてAI解析を行う
 */
export const PDFAnalyzer: FC<PDFAnalyzerProps> = ({ userId, onAnalysisComplete }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    loading,
    error,
    selectedFile,
    setFile,
    analyze,
    setError,
  } = usePDFAnalysisStore();

  /**
   * ファイル選択ハンドラ
   */
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // ファイルサイズチェック
      const maxSizeBytes = PDF_ANALYSIS_CONFIG.maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setError(`ファイルサイズが大きすぎます。最大${PDF_ANALYSIS_CONFIG.maxFileSizeMB}MBまでです。`);
        return;
      }

      // ファイルタイプチェック
      if (!PDF_ANALYSIS_CONFIG.supportedTypes.includes(file.type as typeof PDF_ANALYSIS_CONFIG.supportedTypes[number])) {
        setError('サポートされていないファイル形式です。PDF、JPEG、PNGに対応しています。');
        return;
      }

      setFile(file);
    },
    [setFile, setError]
  );

  /**
   * ドラッグ&ドロップハンドラ
   */
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const file = event.dataTransfer.files?.[0];
      if (!file) return;

      // ファイルサイズチェック
      const maxSizeBytes = PDF_ANALYSIS_CONFIG.maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setError(`ファイルサイズが大きすぎます。最大${PDF_ANALYSIS_CONFIG.maxFileSizeMB}MBまでです。`);
        return;
      }

      // ファイルタイプチェック
      if (!PDF_ANALYSIS_CONFIG.supportedTypes.includes(file.type as typeof PDF_ANALYSIS_CONFIG.supportedTypes[number])) {
        setError('サポートされていないファイル形式です。PDF、JPEG、PNGに対応しています。');
        return;
      }

      setFile(file);
    },
    [setFile, setError]
  );

  /**
   * ドラッグオーバーハンドラ
   */
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  /**
   * 解析実行
   */
  const handleAnalyze = async () => {
    await analyze(userId);
    onAnalysisComplete?.();
  };

  /**
   * ファイル選択をクリア
   */
  const handleClearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box
      p={4}
      bg="blue.50"
      borderRadius="lg"
      border="1px"
      borderColor="blue.200"
    >
      <VStack gap={4} align="stretch">
        {/* ヘッダー */}
        <HStack gap={2}>
          <FiFileText size={20} color="#3182CE" />
          <Text fontWeight="medium" color="blue.700">
            ドキュメントから情報を自動取得
          </Text>
        </HStack>

        {/* ファイルアップロードエリア */}
        <Box
          p={6}
          bg="white"
          borderRadius="md"
          border="2px dashed"
          borderColor={selectedFile ? 'green.400' : 'gray.300'}
          textAlign="center"
          cursor="pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
          transition="all 0.2s"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {selectedFile ? (
            <VStack gap={2}>
              <Badge colorPalette="green" px={3} py={1}>
                ファイル選択済み
              </Badge>
              <HStack gap={2} justify="center">
                <FiFileText />
                <Text fontSize="sm" fontWeight="medium">
                  {selectedFile.name}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFile();
                  }}
                >
                  <FiX />
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Text>
            </VStack>
          ) : (
            <VStack gap={2}>
              <FiUpload size={32} color="#A0AEC0" />
              <Text color="gray.600" fontSize="sm">
                ファイルをドロップまたはクリックして選択
              </Text>
              <Text color="gray.500" fontSize="xs">
                対応形式: PDF, JPEG, PNG（最大{PDF_ANALYSIS_CONFIG.maxFileSizeMB}MB）
              </Text>
            </VStack>
          )}
        </Box>

        {/* エラー表示 */}
        {error && (
          <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
            <Text fontSize="sm" color="red.700">
              {error}
            </Text>
          </Box>
        )}

        {/* 解析ボタン */}
        <Button
          colorPalette="blue"
          disabled={!selectedFile || loading}
          loading={loading}
          onClick={handleAnalyze}
        >
          <HStack gap={2}>
            <FiSearch />
            <Text>{loading ? '解析中...' : '解析を開始'}</Text>
          </HStack>
        </Button>

        {/* 注意事項 */}
        <Text fontSize="xs" color="gray.500" textAlign="center">
          この機能はAIによる自動認識です。抽出された情報は必ず確認してください。
        </Text>
      </VStack>
    </Box>
  );
};
